import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ClipboardList, Loader2, Sparkles, Clock, AlertTriangle, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/architect`;

async function j(path, method = "GET", body) {
  const token = localStorage.getItem("arca_token");
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { let d = "Request failed"; try { d = (await res.json()).detail || d; } catch {} throw new Error(d); }
  return res.json();
}

const PRIO = { high: "text-red-400 border-red-500/40", medium: "text-amber-400 border-amber-500/40", low: "text-slate-400 border-white/15" };

export default function ProjectPlanPanel({ projectId, initialPlan }) {
  const [plan, setPlan] = useState(initialPlan || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPlan(initialPlan || null); }, [initialPlan]);

  const buildPlan = useCallback(async () => {
    setBusy(true);
    try {
      const p = await j(`/projects/${projectId}/plan`, "POST");
      setPlan(p);
      toast.success("AI project plan ready");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [projectId]);

  const toggle = async (task) => {
    const next = !task.done;
    setPlan((pl) => ({
      ...pl,
      milestones: pl.milestones.map((m) => ({ ...m, tasks: m.tasks.map((t) => (t.id === task.id ? { ...t, done: next } : t)) })),
    }));
    try {
      const prog = await j(`/projects/${projectId}/plan/task`, "POST", { task_id: task.id, done: next });
      setPlan((pl) => ({ ...pl, progress: prog }));
    } catch (e) { toast.error(e.message); }
  };

  const pct = plan?.progress?.percent ?? 0;

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="project-plan-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="font-display font-bold text-lg flex items-center gap-2"><ClipboardList size={18} className="text-[#00F0FF]" /> AI Project Manager</p>
        <button onClick={buildPlan} disabled={busy} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-[0_0_25px_rgba(0,85,255,0.4)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="project-plan-run-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {plan ? "Re-plan" : "Generate plan"}
        </button>
      </div>

      {!plan && <p className="text-slate-500 text-sm">Let the AI break this project into milestones and tasks with time estimates, blockers and a recommended next step.</p>}

      {plan && (
        <div data-testid="project-plan-result">
          {/* progress + estimate */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5 text-slate-300 text-sm"><Clock size={14} className="text-[#00F0FF]" /> Est. <b className="text-white">{plan.total_estimate_hours}h</b></div>
            <div className="flex-1 min-w-[160px]">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1"><span>Progress</span><span data-testid="plan-progress-pct">{pct}% · {plan.progress?.completed_tasks}/{plan.progress?.total_tasks}</span></div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} className="h-full bg-[#00F0FF]" />
              </div>
            </div>
          </div>

          {/* recommended next */}
          {plan.recommended_next?.task_title && (
            <div className="mt-4 rounded-xl border border-[#0055FF]/25 bg-[#0055FF]/10 p-3.5 flex items-start gap-2.5" data-testid="plan-recommended-next">
              <ArrowRightCircle size={16} className="text-[#00F0FF] mt-0.5 shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">Next: {plan.recommended_next.task_title}</p>
                <p className="text-slate-400 text-xs mt-0.5">{plan.recommended_next.why}</p>
              </div>
            </div>
          )}

          {/* blockers */}
          {(plan.blockers || []).length > 0 && (
            <div className="mt-4 space-y-2" data-testid="plan-blockers">
              {plan.blockers.map((b, i) => (
                <div key={i} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5">
                  <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                  <div><p className="text-white text-sm font-medium">{b.title}</p><p className="text-slate-400 text-xs mt-0.5">{b.detail}</p></div>
                </div>
              ))}
            </div>
          )}

          {/* milestones + tasks */}
          <div className="mt-4 space-y-4" data-testid="plan-milestones">
            {(plan.milestones || []).map((m, mi) => (
              <div key={mi}>
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400 mb-2">{m.title}</p>
                <div className="space-y-1.5">
                  {(m.tasks || []).map((t) => (
                    <label key={t.id} className="flex items-center gap-3 rounded-lg bg-black/30 px-3 py-2 cursor-pointer hover:bg-black/50 transition-colors duration-200" data-testid={`plan-task-${t.id}`}>
                      <input type="checkbox" checked={!!t.done} onChange={() => toggle(t)} className="accent-[#0055FF] w-4 h-4 shrink-0" data-testid={`plan-task-check-${t.id}`} />
                      <span className={`flex-1 text-sm ${t.done ? "line-through text-slate-500" : "text-slate-200"}`}>{t.title}</span>
                      <span className="text-slate-500 text-xs font-mono">{t.estimate_hours}h</span>
                      <span className={`text-[9px] font-mono uppercase border rounded px-1.5 py-0.5 ${PRIO[t.priority] || PRIO.low}`}>{t.priority}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
